import React, { useContext, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferContext } from '../../contextStore/OfferContext';
import OfferCard from './OfferCard';
import './OffersPage.css';

function ReceivedOffers() {
  const { t } = useTranslation();
  const { receivedOffers } = useContext(OfferContext);
  const [filter, setFilter] = useState('all');

  const FILTERS = useMemo(() => [
    { value: 'all', label: t('common.all') },
    { value: 'pending', label: t('offers.statusPending') },
    { value: 'accepted', label: t('offers.statusAccepted') },
    { value: 'countered', label: t('offers.statusCountered') },
    { value: 'rejected', label: t('offers.statusDeclined') },
  ], [t]);

  const filtered = useMemo(() => {
    if (filter === 'all') return receivedOffers;
    return receivedOffers.filter((o) => o.status === filter);
  }, [receivedOffers, filter]);

  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, countered: 0, rejected: 0 };
    receivedOffers.forEach((o) => {
      if (c[o.status] !== undefined) c[o.status]++;
    });
    return c;
  }, [receivedOffers]);

  const pendingCount = counts.pending;

  return (
    <div className="offersPage">
      {/* Header */}
      <div className="offersPage__header">
        <div className="offersPage__headerTop">
          <div>
            <h2 className="offersPage__title">
              {t('offers.receivedOffers')}
              {pendingCount > 0 && (
                <span className="offersPage__pendingBadge">{pendingCount} {t('offers.new')}</span>
              )}
            </h2>
            <p className="offersPage__subtitle">{t('offers.offersSubtitle')}</p>
          </div>
          {receivedOffers.length > 0 && (
            <span className="offersPage__count">{receivedOffers.length}</span>
          )}
        </div>

        {/* Stats */}
        {receivedOffers.length > 0 && (
          <div className="offersPage__stats">
            <div className="offersPage__stat offersPage__stat--pending">
              <span className="offersPage__statValue">{counts.pending}</span>
              <span className="offersPage__statLabel">{t('offers.statusPending')}</span>
            </div>
            <div className="offersPage__stat offersPage__stat--accepted">
              <span className="offersPage__statValue">{counts.accepted}</span>
              <span className="offersPage__statLabel">{t('offers.statusAccepted')}</span>
            </div>
            <div className="offersPage__stat offersPage__stat--countered">
              <span className="offersPage__statValue">{counts.countered}</span>
              <span className="offersPage__statLabel">{t('offers.statusCountered')}</span>
            </div>
            <div className="offersPage__stat offersPage__stat--rejected">
              <span className="offersPage__statValue">{counts.rejected}</span>
              <span className="offersPage__statLabel">{t('offers.statusDeclined')}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        {receivedOffers.length > 1 && (
          <div className="offersPage__filters">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`offersPage__filterBtn ${filter === f.value ? 'offersPage__filterBtn--active' : ''}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
                {f.value !== 'all' && counts[f.value] > 0 && (
                  <span className="offersPage__filterCount">{counts[f.value]}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pending action banner */}
      {pendingCount > 0 && filter === 'all' && (
        <div className="offersPage__actionBanner">
          <div className="offersPage__actionBannerIcon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="offersPage__actionBannerText">
            <strong>{t('offers.awaitingResponse', { count: pendingCount })}</strong>
            <span>{t('offers.reviewRespond')}</span>
          </div>
        </div>
      )}

      {/* Content */}
      {receivedOffers.length === 0 ? (
        <div className="offersPage__empty">
          <div className="offersPage__emptyIcon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h3 className="offersPage__emptyTitle">{t('ui.noOffersReceived')}</h3>
          <p className="offersPage__emptyText">
            {t('ui.offersWillAppear')}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="offersPage__empty offersPage__empty--small">
          <p className="offersPage__emptyText">{t('offers.noOffersFound', { filter: t(`offers.status${filter.charAt(0).toUpperCase() + filter.slice(1)}`) })}</p>
        </div>
      ) : (
        <div className="offersPage__list">
          {filtered.map((offer) => (
            <OfferCard key={offer.id} offer={offer} isSeller />
          ))}
        </div>
      )}
    </div>
  );
}

export default ReceivedOffers;
